import { enqueue, placeBuilding, worldToTile } from '../sim'
import type { GridGeometry, SimState, Tile } from '../sim'
import type { ContentId } from '../content'

// Placement input adapter. This is the ONE place the screen->world transform
// lives: a canvas click is converted screen -> world -> tile here, and only a
// tile index ever reaches the command (never a raw pixel), so the placement is
// viewport-independent. `world` is the FIXED sim-world region the canvas shows;
// the canvas size (`viewport`) enters only as the scale between them, so the
// same world position resolves to the same tile on any canvas size — which is
// exactly what the determinism spec proves. Input never mutates sim state; it
// only enqueues a typed command for the phase-1 drain to validate.

/** A width/height extent in pixels (viewport) or world units (world region). */
export interface Extent {
  width: number
  height: number
}

/**
 * Convert a canvas pixel position to a world coordinate. The canvas size enters
 * only as the `world/viewport` scale, so a given fractional canvas position maps
 * to the same world point on any canvas — the render adapter scales world->canvas,
 * this inverts it against the same fixed world region.
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  viewport: Extent,
  world: Extent,
): { x: number; y: number } {
  return {
    x: screenX * (world.width / viewport.width),
    y: screenY * (world.height / viewport.height),
  }
}

/**
 * Convert a canvas pixel position to the grid tile it falls on. A degenerate
 * viewport (zero width/height — a hidden tab, a minimized or not-yet-laid-out
 * canvas) would make the screen->world scale divide by zero, so it returns a
 * fixed out-of-bounds tile: the drain rejects it and the render draws it as a
 * not-placeable tile, never a NaN/Infinity position. `grid` need only supply the
 * tile geometry, so a read-only snapshot grid works as well as the live one.
 */
export function screenToTile(
  screenX: number,
  screenY: number,
  viewport: Extent,
  world: Extent,
  grid: GridGeometry,
): Tile {
  if (viewport.width <= 0 || viewport.height <= 0) return { tx: -1, ty: -1 }
  return worldToTile(grid, screenToWorld(screenX, screenY, viewport, world))
}

/**
 * Translate a canvas click into a place_building command on the queue. Returns
 * the resolved tile so the caller can show placement feedback; it does not touch
 * sim state — the drain decides whether the placement is accepted or rejected.
 * `grid` is the geometry the transform resolves against (the render passes its
 * snapshot grid, keeping input off any live sim reference); only the queue is
 * touched, and only to enqueue.
 */
export function enqueuePlacement(
  state: SimState,
  grid: GridGeometry,
  id: ContentId,
  screenX: number,
  screenY: number,
  viewport: Extent,
  world: Extent,
): Tile {
  const tile = screenToTile(screenX, screenY, viewport, world, grid)
  enqueue(state.commandQueue, placeBuilding(id, tile))
  return tile
}
